// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Craps
// Full craps table with provably fair dice rolling
// ================================================================
import React, { useState, useCallback, useRef, useEffect } from 'react';
import ProvablyFairEngine from '../../engine/ProvablyFairEngine';
import { useCasino } from '../../context/CasinoShell';

const DICE_FACES = {
  1: [{ x: 50, y: 50 }],
  2: [{ x: 25, y: 25 }, { x: 75, y: 75 }],
  3: [{ x: 25, y: 25 }, { x: 50, y: 50 }, { x: 75, y: 75 }],
  4: [{ x: 25, y: 25 }, { x: 75, y: 25 }, { x: 25, y: 75 }, { x: 75, y: 75 }],
  5: [{ x: 25, y: 25 }, { x: 75, y: 25 }, { x: 50, y: 50 }, { x: 25, y: 75 }, { x: 75, y: 75 }],
  6: [{ x: 25, y: 20 }, { x: 75, y: 20 }, { x: 25, y: 50 }, { x: 75, y: 50 }, { x: 25, y: 80 }, { x: 75, y: 80 }],
};

const BET_OPTIONS = [1, 5, 10, 25, 50];

export default function Craps() {
  const { forgeBalance, placeBet, recordWin, recordLoss, logFairness, isExcluded } = useCasino();
  const engineRef = useRef(new ProvablyFairEngine());

  const [bet, setBet] = useState(5);
  const [betType, setBetType] = useState('pass'); // pass, dontpass, field, any7, anyCraps
  const [dice, setDice] = useState([null, null]);
  const [rolling, setRolling] = useState(false);
  const [point, setPoint] = useState(null); // null = come-out roll, number = point established
  const [result, setResult] = useState(null);
  const [lastWin, setLastWin] = useState(null);
  const [commitmentHash, setCommitmentHash] = useState(null);
  const [rollHistory, setRollHistory] = useState([]);
  const [history, setHistory] = useState([]);
  const [rollAnim, setRollAnim] = useState(false);

  useEffect(() => {
    async function init() {
      const commitment = await engineRef.current.createCommitment();
      setCommitmentHash(commitment.commitmentHash);
    }
    init();
  }, []);

  const rollDice = useCallback(async () => {
    if (rolling) return;
    if (isExcluded()) return;

    const betResult = placeBet(bet, 'craps');
    if (!betResult.success) return;

    setRolling(true);
    setResult(null);
    setLastWin(null);
    setRollAnim(true);

    const clientSeed = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    const fairResult = await engineRef.current.generateResult(clientSeed);
    const proof = engineRef.current.getProof();

    // Map to dice (1-6 each)
    const die1 = Math.floor(fairResult.resultValues[0] * 6) + 1;
    const die2 = Math.floor(fairResult.resultValues[1] * 6) + 1;
    const total = die1 + die2;

    // Animate dice rolling
    for (let i = 0; i < 8; i++) {
      await new Promise(r => setTimeout(r, 100));
      setDice([Math.floor(Math.random() * 6) + 1, Math.floor(Math.random() * 6) + 1]);
    }

    // Show final result
    setDice([die1, die2]);
    setRollAnim(false);

    await new Promise(r => setTimeout(r, 300));

    // Resolve bets
    let winAmount = 0;
    let outcome = '';

    if (betType === 'pass') {
      if (!point) {
        // Come-out roll
        if (total === 7 || total === 11) {
          winAmount = bet * 2;
          outcome = total === 7 ? 'NATURAL 7!' : 'YO-ELEVEN!';
        } else if (total === 2 || total === 3 || total === 12) {
          outcome = 'CRAPS!';
        } else {
          setPoint(total);
          outcome = `POINT IS ${total}`;
          // Refund bet — no resolution yet
          recordWin(bet, 'craps', proof.resultHash); // Return original bet
          setRolling(false);
          setRollHistory(prev => [{ die1, die2, total }, ...prev].slice(0, 20));
          logFairness({ gameId: 'craps', ...proof, betAmount: bet });
          const next = await engineRef.current.createCommitment();
          setCommitmentHash(next.commitmentHash);
          setResult({ text: outcome, type: 'point' });
          return;
        }
      } else {
        // Point established
        if (total === point) {
          winAmount = bet * 2;
          outcome = `HIT THE POINT! ${total}!`;
          setPoint(null);
        } else if (total === 7) {
          outcome = 'SEVEN OUT!';
          setPoint(null);
        } else {
          outcome = `ROLLED ${total} — POINT IS ${point}`;
          // Still alive, refund
          recordWin(bet, 'craps', proof.resultHash);
          setRolling(false);
          setRollHistory(prev => [{ die1, die2, total }, ...prev].slice(0, 20));
          logFairness({ gameId: 'craps', ...proof, betAmount: bet });
          const next = await engineRef.current.createCommitment();
          setCommitmentHash(next.commitmentHash);
          setResult({ text: outcome, type: 'continue' });
          return;
        }
      }
    } else if (betType === 'dontpass') {
      if (!point) {
        if (total === 2 || total === 3) {
          winAmount = bet * 2;
          outcome = total === 2 ? 'SNAKE EYES — YOU WIN!' : 'CRAPS 3 — YOU WIN!';
        } else if (total === 12) {
          winAmount = bet; // Push on 12
          outcome = 'PUSH (12)';
        } else if (total === 7 || total === 11) {
          outcome = total === 7 ? 'SEVEN — YOU LOSE' : 'YO — YOU LOSE';
        } else {
          setPoint(total);
          outcome = `POINT IS ${total}`;
          recordWin(bet, 'craps', proof.resultHash);
          setRolling(false);
          setRollHistory(prev => [{ die1, die2, total }, ...prev].slice(0, 20));
          const next = await engineRef.current.createCommitment();
          setCommitmentHash(next.commitmentHash);
          setResult({ text: outcome, type: 'point' });
          return;
        }
      } else {
        if (total === 7) {
          winAmount = bet * 2;
          outcome = 'SEVEN OUT — YOU WIN!';
          setPoint(null);
        } else if (total === point) {
          outcome = `POINT HIT — YOU LOSE`;
          setPoint(null);
        } else {
          outcome = `ROLLED ${total} — POINT IS ${point}`;
          recordWin(bet, 'craps', proof.resultHash);
          setRolling(false);
          setRollHistory(prev => [{ die1, die2, total }, ...prev].slice(0, 20));
          const next = await engineRef.current.createCommitment();
          setCommitmentHash(next.commitmentHash);
          setResult({ text: outcome, type: 'continue' });
          return;
        }
      }
    } else if (betType === 'field') {
      if ([2, 3, 4, 9, 10, 11, 12].includes(total)) {
        const multi = total === 2 ? 3 : total === 12 ? 3 : 2;
        winAmount = bet * multi;
        outcome = total === 2 || total === 12 ? `FIELD ${total} — TRIPLE!` : `FIELD ${total}!`;
      } else {
        outcome = `${total} — FIELD LOSES`;
      }
    } else if (betType === 'any7') {
      if (total === 7) {
        winAmount = bet * 5;
        outcome = 'ANY SEVEN — 4:1!';
      } else {
        outcome = `${total} — NOT SEVEN`;
      }
    } else if (betType === 'anyCraps') {
      if (total === 2 || total === 3 || total === 12) {
        winAmount = bet * 8;
        outcome = `CRAPS ${total} — 7:1!`;
      } else {
        outcome = `${total} — NO CRAPS`;
      }
    }

    if (winAmount > 0) {
      recordWin(winAmount, 'craps', proof.resultHash);
      setLastWin(winAmount - bet);
    } else {
      recordLoss(bet, 'craps', proof.resultHash);
    }

    setResult({ text: outcome, type: winAmount > 0 ? 'win' : 'lose', winAmount });
    setRollHistory(prev => [{ die1, die2, total }, ...prev].slice(0, 20));
    setHistory(prev => [{
      total, betType, bet,
      win: winAmount > bet ? winAmount - bet : 0,
      outcome,
      time: new Date().toLocaleTimeString(),
    }, ...prev].slice(0, 15));

    logFairness({ gameId: 'craps', ...proof, betAmount: bet, winAmount });

    setRolling(false);
    const next = await engineRef.current.createCommitment();
    setCommitmentHash(next.commitmentHash);
  }, [rolling, bet, betType, point, placeBet, isExcluded, recordWin, recordLoss, logFairness]);

  const renderDie = (value, index) => {
    if (!value) return (
      <div key={index} style={ds.dieEmpty}>{'🎲'}</div>
    );
    return (
      <div key={index} style={{
        ...ds.die,
        animation: rollAnim ? `diceShake 0.1s ease-in-out infinite ${index * 0.05}s` : 'none',
      }}>
        <svg viewBox="0 0 100 100" width="100%" height="100%">
          {DICE_FACES[value].map((dot, i) => (
            <circle key={i} cx={dot.x} cy={dot.y} r={9}
              fill="#1E293B" stroke="#0F172A" strokeWidth={1} />
          ))}
        </svg>
      </div>
    );
  };

  const BET_TYPES = [
    { id: 'pass', label: 'PASS', desc: 'Win on 7/11, lose on 2/3/12', color: '#22C55E' },
    { id: 'dontpass', label: "DON'T PASS", desc: 'Opposite of Pass line', color: '#EF4444' },
    { id: 'field', label: 'FIELD', desc: '2,3,4,9,10,11,12 win', color: '#EAB308' },
    { id: 'any7', label: 'ANY 7', desc: 'Pays 4:1 on any 7', color: '#3B82F6' },
    { id: 'anyCraps', label: 'CRAPS', desc: 'Pays 7:1 on 2/3/12', color: '#A855F7' },
  ];

  return (
    <div style={ds.container}>
      <style>{`
        @keyframes diceShake { 0%{transform:rotate(0deg)} 25%{transform:rotate(10deg)} 75%{transform:rotate(-10deg)} 100%{transform:rotate(0deg)} }
        @keyframes diceBounce { 0%{transform:scale(0.5) rotate(720deg);opacity:0} 60%{transform:scale(1.1) rotate(0deg);opacity:1} 100%{transform:scale(1) rotate(0deg);opacity:1} }
      `}</style>

      {/* Header */}
      <div style={ds.header}>
        <h2 style={ds.title}>CRAPS</h2>
        <div style={ds.headerInfo}>
          <span style={ds.balVal}>${forgeBalance.toFixed(2)}</span>
          {point && <span style={ds.pointBadge}>POINT: {point}</span>}
        </div>
      </div>

      {/* Dice area */}
      <div style={ds.diceSection}>
        <div style={ds.feltGlow} />
        <div style={ds.diceRow}>
          {renderDie(dice[0], 0)}
          {renderDie(dice[1], 1)}
        </div>
        {dice[0] && dice[1] && !rollAnim && (
          <div style={ds.totalBadge}>
            <span style={ds.totalNum}>{dice[0] + dice[1]}</span>
          </div>
        )}

        {/* Result */}
        {result && (
          <div style={{
            ...ds.resultBanner,
            borderColor: result.type === 'win' ? '#22C55E' : result.type === 'lose' ? '#EF4444' : '#EAB308',
            background: result.type === 'win'
              ? 'linear-gradient(135deg, #14532D 0%, #166534 100%)'
              : result.type === 'lose'
                ? 'linear-gradient(135deg, #7F1D1D 0%, #991B1B 100%)'
                : 'linear-gradient(135deg, #422006 0%, #713F12 100%)',
          }}>
            <span style={ds.resultText}>{result.text}</span>
            {result.winAmount > 0 && result.winAmount > bet && (
              <span style={ds.resultWin}>+${(result.winAmount - bet).toFixed(2)}</span>
            )}
          </div>
        )}

        {/* Roll history */}
        {rollHistory.length > 0 && (
          <div style={ds.rollHist}>
            {rollHistory.slice(0, 12).map((r, i) => (
              <span key={i} style={{
                ...ds.rollDot,
                background: r.total === 7 ? '#EF4444' : r.total === 11 ? '#22C55E' : [2,3,12].includes(r.total) ? '#A855F7' : '#334155',
                opacity: 1 - (i * 0.06),
              }}>{r.total}</span>
            ))}
          </div>
        )}
      </div>

      {/* Bet type selection */}
      <div style={ds.betTypeSection}>
        {BET_TYPES.map(bt => (
          <button key={bt.id} style={{
            ...ds.betTypeBtn,
            borderColor: betType === bt.id ? bt.color : '#334155',
            background: betType === bt.id ? bt.color + '22' : '#0F172A',
          }} onClick={() => { if (!rolling && !point) setBetType(bt.id); }}
            disabled={(point && (bt.id !== 'pass' && bt.id !== 'dontpass' && bt.id !== 'field' && bt.id !== 'any7' && bt.id !== 'anyCraps'))}>
            <span style={{ ...ds.betTypeName, color: betType === bt.id ? bt.color : '#94A3B8' }}>{bt.label}</span>
            <span style={ds.betTypeDesc}>{bt.desc}</span>
          </button>
        ))}
      </div>

      {/* Bet amount + roll */}
      <div style={ds.controls}>
        <div style={ds.betAmountRow}>
          {BET_OPTIONS.map(b => (
            <button key={b} style={{
              ...ds.betChip,
              background: bet === b
                ? 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)'
                : 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
              borderColor: bet === b ? '#EF4444' : '#334155',
            }} onClick={() => setBet(b)} disabled={rolling}>
              ${b}
            </button>
          ))}
        </div>

        <button style={{
          ...ds.rollBtn,
          opacity: rolling || forgeBalance < bet ? 0.4 : 1,
        }} onClick={rollDice} disabled={rolling || forgeBalance < bet}>
          {rolling ? 'ROLLING...' : point ? `ROLL • $${bet} (Pt: ${point})` : `ROLL • $${bet}`}
        </button>
      </div>

      {/* Footer */}
      <div style={ds.footer}>
        <span>Casino Craps • Pass/Don't Pass • Field • Props • Provably Fair</span>
      </div>
    </div>
  );
}

const ds = {
  container: { fontFamily: "'SF Pro Display', -apple-system, sans-serif" },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px',
    background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
    borderBottom: '1px solid #334155',
  },
  title: { fontSize: 16, fontWeight: 800, color: '#F8FAFC', letterSpacing: 4, margin: 0 },
  headerInfo: { display: 'flex', alignItems: 'center', gap: 10 },
  balVal: { fontSize: 14, fontWeight: 800, color: '#FFD700', fontFamily: 'monospace' },
  pointBadge: {
    padding: '3px 10px', borderRadius: 12,
    background: '#EAB30833', border: '1px solid #EAB308',
    fontSize: 10, fontWeight: 800, color: '#EAB308', letterSpacing: 1,
  },
  diceSection: {
    position: 'relative',
    padding: '24px 16px 16px',
    background: 'linear-gradient(180deg, #14532D 0%, #166534 50%, #14532D 100%)',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    borderBottom: '3px solid #854D0E',
    minHeight: 180, overflow: 'hidden',
  },
  feltGlow: {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 200, height: 200,
    background: 'radial-gradient(ellipse, rgba(255,255,255,0.05) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  diceRow: {
    display: 'flex', gap: 16, marginBottom: 12,
    position: 'relative', zIndex: 1,
  },
  die: {
    width: 64, height: 64, borderRadius: 12,
    background: 'linear-gradient(180deg, #FFFFFF 0%, #E2E8F0 100%)',
    border: '2px solid #CBD5E1',
    padding: 6,
    boxShadow: '0 6px 16px rgba(0,0,0,0.4), 0 2px 4px rgba(0,0,0,0.2)',
  },
  dieEmpty: {
    width: 64, height: 64, borderRadius: 12,
    background: 'rgba(255,255,255,0.1)',
    border: '2px dashed rgba(255,255,255,0.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 28,
  },
  totalBadge: {
    padding: '4px 16px', borderRadius: 20,
    background: 'rgba(0,0,0,0.5)',
    border: '1px solid rgba(255,255,255,0.15)',
    marginBottom: 8,
  },
  totalNum: { fontSize: 18, fontWeight: 900, color: '#FFD700', fontFamily: 'monospace' },
  resultBanner: {
    padding: '8px 20px', borderRadius: 12,
    border: '2px solid', marginBottom: 8,
    boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
    textAlign: 'center',
  },
  resultText: { fontSize: 12, fontWeight: 900, color: '#F8FAFC', letterSpacing: 1, display: 'block' },
  resultWin: { fontSize: 14, fontWeight: 900, color: '#FFD700', display: 'block', marginTop: 2 },
  rollHist: {
    display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center',
    position: 'relative', zIndex: 1,
  },
  rollDot: {
    width: 22, height: 22, borderRadius: 4,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 9, fontWeight: 800, color: '#F8FAFC',
    border: '1px solid rgba(255,255,255,0.15)',
  },
  betTypeSection: {
    padding: '8px 12px',
    background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  betTypeBtn: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 12px', borderRadius: 8,
    border: '1px solid', cursor: 'pointer',
    transition: 'all 0.15s',
  },
  betTypeName: { fontSize: 11, fontWeight: 800, letterSpacing: 1 },
  betTypeDesc: { fontSize: 8, color: '#64748B' },
  controls: {
    padding: '12px 16px',
    background: '#0F172A',
    borderTop: '1px solid #1E293B',
  },
  betAmountRow: {
    display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 10,
  },
  betChip: {
    width: 44, height: 44, borderRadius: '50%',
    border: '2px solid',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 9, fontWeight: 800, color: '#F8FAFC',
    cursor: 'pointer', transition: 'all 0.15s',
  },
  rollBtn: {
    width: '100%', padding: '14px',
    borderRadius: 12, border: '2px solid #DC2626',
    background: 'linear-gradient(180deg, #DC2626 0%, #B91C1C 100%)',
    color: '#fff', fontSize: 14, fontWeight: 900,
    letterSpacing: 2, cursor: 'pointer',
    boxShadow: '0 4px 16px #DC262644',
  },
  footer: {
    padding: '10px 16px', textAlign: 'center',
    fontSize: 8, color: '#475569', letterSpacing: 1,
    background: '#0F172A',
  },
};
